/**
 * Shared pricing editor for all "electrical-style" service categories:
 * Electrical, Landscaping, Kitchens & Bathrooms, Locks & Security,
 * Plumbing, Property Maintenance, Roofing, Finishing & Interiors
 *
 * All these categories store data in:
 *   electrical_pricing_variables
 *   electrical_decision_nodes
 *   electrical_pricing_options
 *
 * Config endpoint:  GET  /admin/pricing/electrical/:serviceId/config
 * Variable save:    PUT  /admin/pricing/electrical/variables/:id
 * Option save:      PUT  /admin/pricing/electrical/options/:id
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import apiService from '@/services/api';

const MULTIPLIER_COLS = [
  { key: 'work_type_multiplier',   label: 'Work Type' },
  { key: 'complexity_multiplier',  label: 'Complexity' },
  { key: 'access_multiplier',      label: 'Access' },
  { key: 'structural_multiplier',  label: 'Structural' },
  { key: 'material_multiplier',    label: 'Material' },
  { key: 'finish_multiplier',      label: 'Finish' },
  { key: 'height_multiplier',      label: 'Height' },
  { key: 'occupation_multiplier',  label: 'Occupation' },
  { key: 'priority_multiplier',    label: 'Priority' },
  { key: 'risk_multiplier',        label: 'Risk' },
] as const;

export interface ServiceMap {
  [id: number]: { name: string; color?: string };
}

interface Props {
  services: ServiceMap;
  title: string;
  description?: string;
}

const AdminElectricalStylePricingEditor: React.FC<Props> = ({ services, title, description }) => {
  const serviceIds = Object.keys(services).map(Number);
  const [selectedId, setSelectedId] = useState<number>(serviceIds[0]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [variables, setVariables] = useState<any[]>([]);
  const [nodes, setNodes] = useState<any[]>([]);
  const [options, setOptions] = useState<any[]>([]);

  // Local edit buffers — keyed by row ID
  const [varEdits, setVarEdits] = useState<Record<number, { min: string; max: string }>>({});
  const [optEdits, setOptEdits] = useState<Record<number, Record<string, string>>>({});

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.request(`/admin/pricing/electrical/${selectedId}/config`, 'GET');
      if (res.success && res.data) {
        setVariables(res.data.variables || []);
        setNodes(res.data.nodes || []);
        setOptions(res.data.options || []);
        setVarEdits({});
        setOptEdits({});
      } else {
        setError('Failed to load config');
      }
    } catch {
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  // ── Variable edits ──────────────────────────────────────────────

  const varVal = (v: any, field: 'min' | 'max') =>
    varEdits[v.id]?.[field] ?? String(field === 'min' ? v.min_value ?? v.variable_value_min : v.max_value ?? v.variable_value_max);

  const setVarField = (id: number, field: 'min' | 'max', val: string) =>
    setVarEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));

  const saveVariable = async (v: any) => {
    const key = `var-${v.id}`;
    setSaving(key);
    try {
      const res = await apiService.request(`/admin/pricing/electrical/variables/${v.id}`, 'PUT', {
        min_value: parseFloat(varEdits[v.id]?.min ?? v.min_value),
        max_value: parseFloat(varEdits[v.id]?.max ?? v.max_value),
      });
      if (res.success) {
        showToast('Variable saved');
        loadConfig();
      } else {
        setError(res.error || 'Save failed');
      }
    } catch {
      setError('Save failed');
    } finally {
      setSaving(null);
    }
  };

  // ── Option edits ────────────────────────────────────────────────

  const optVal = (opt: any, col: string) =>
    optEdits[opt.id]?.[col] ?? String(opt[col] ?? '');

  const setOptField = (id: number, col: string, val: string) =>
    setOptEdits(prev => ({ ...prev, [id]: { ...prev[id], [col]: val } }));

  const saveOption = async (opt: any, fields: string[]) => {
    const key = `opt-${opt.id}`;
    setSaving(key);
    try {
      const body: Record<string, number> = {};
      for (const f of fields) {
        const raw = optEdits[opt.id]?.[f] ?? String(opt[f] ?? 0);
        body[f] = parseFloat(raw);
      }
      const res = await apiService.request(`/admin/pricing/electrical/options/${opt.id}`, 'PUT', body);
      if (res.success) {
        showToast('Saved');
        loadConfig();
      } else {
        setError(res.error || 'Save failed');
      }
    } catch {
      setError('Save failed');
    } finally {
      setSaving(null);
    }
  };

  // ── Derived helpers ─────────────────────────────────────────────

  const optionsForNode = (nodeId: number) => options.filter(o => o.node_id === nodeId);

  // Only show multiplier columns that have at least one non-1.0 value in current options
  const activeMulCols = MULTIPLIER_COLS.filter(c =>
    options.some(o => o[c.key] !== undefined && o[c.key] !== null)
  );

  const serviceName = services[selectedId]?.name || 'Unknown';

  return (
    <div className="space-y-4 p-4">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-2 rounded shadow-lg text-sm">
          {toast}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{title}</span>
            <span className="text-sm font-normal text-gray-500">{serviceName}</span>
          </CardTitle>
          {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Service selector */}
          <div className="flex flex-wrap gap-2">
            {serviceIds.map(id => (
              <Button
                key={id}
                size="sm"
                variant={selectedId === id ? 'default' : 'outline'}
                onClick={() => { setSelectedId(id); setActiveTab('overview'); }}
              >
                {services[id]?.name}
              </Button>
            ))}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <p className="text-gray-500 text-sm py-4 text-center">Loading…</p>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="variables">Variables</TabsTrigger>
                <TabsTrigger value="multipliers">Multipliers</TabsTrigger>
                <TabsTrigger value="fixedcosts">Fixed Costs</TabsTrigger>
                <TabsTrigger value="questions">Questions</TabsTrigger>
              </TabsList>

              {/* ── OVERVIEW ─────────────────────────────────────── */}
              <TabsContent value="overview">
                <Card>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="rounded border p-4">
                        <p className="text-2xl font-bold">{variables.length}</p>
                        <p className="text-xs text-gray-500 mt-1">Variables</p>
                      </div>
                      <div className="rounded border p-4">
                        <p className="text-2xl font-bold">{nodes.length}</p>
                        <p className="text-xs text-gray-500 mt-1">Decision Nodes</p>
                      </div>
                      <div className="rounded border p-4">
                        <p className="text-2xl font-bold">{options.length}</p>
                        <p className="text-xs text-gray-500 mt-1">Options</p>
                      </div>
                    </div>
                    <Table className="mt-4">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Node</TableHead>
                          <TableHead>Question</TableHead>
                          <TableHead>Options</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {nodes.map(n => (
                          <TableRow key={n.id}>
                            <TableCell className="font-medium text-sm">{n.node_number}. {n.node_title}</TableCell>
                            <TableCell className="text-sm text-gray-500">{n.node_question}</TableCell>
                            <TableCell><Badge variant="secondary">{optionsForNode(n.id).length}</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ── VARIABLES ────────────────────────────────────── */}
              <TabsContent value="variables">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Base Pricing Variables</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {variables.length === 0 ? (
                      <p className="text-sm text-gray-500">No variables configured</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Variable</TableHead>
                            <TableHead>Unit</TableHead>
                            <TableHead>Min ($)</TableHead>
                            <TableHead>Max ($)</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {variables.map(v => (
                            <TableRow key={v.id}>
                              <TableCell className="font-medium text-sm">{v.variable_name}</TableCell>
                              <TableCell className="text-xs text-gray-500">{v.unit || '—'}</TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={varVal(v, 'min')}
                                  onChange={e => setVarField(v.id, 'min', e.target.value)}
                                  className="w-24 h-8 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={varVal(v, 'max')}
                                  onChange={e => setVarField(v.id, 'max', e.target.value)}
                                  className="w-24 h-8 text-sm"
                                />
                              </TableCell>
                              <TableCell className="text-xs text-gray-500 max-w-xs">{v.description || '—'}</TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  disabled={saving === `var-${v.id}`}
                                  onClick={() => saveVariable(v)}
                                >
                                  {saving === `var-${v.id}` ? '…' : 'Save'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ── MULTIPLIERS ──────────────────────────────────── */}
              <TabsContent value="multipliers">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Pricing Multipliers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {options.length === 0 ? (
                      <p className="text-sm text-gray-500">No options configured</p>
                    ) : (
                      <div className="space-y-6">
                        {nodes.map(node => {
                          const nodeOpts = optionsForNode(node.id);
                          if (!nodeOpts.length) return null;
                          return (
                            <div key={node.id}>
                              <h4 className="font-semibold text-sm mb-2">
                                Node {node.node_number}: {node.node_title}
                              </h4>
                              <div className="overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="min-w-[160px]">Option</TableHead>
                                      {activeMulCols.map(c => (
                                        <TableHead key={c.key} className="text-xs min-w-[90px]">{c.label}</TableHead>
                                      ))}
                                      <TableHead></TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {nodeOpts.map(opt => (
                                      <TableRow key={opt.id}>
                                        <TableCell className="font-medium text-sm">{opt.option_label}</TableCell>
                                        {activeMulCols.map(c => (
                                          <TableCell key={c.key}>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              value={optVal(opt, c.key)}
                                              onChange={e => setOptField(opt.id, c.key, e.target.value)}
                                              className={`w-20 h-8 text-sm ${parseFloat(optVal(opt, c.key)) !== 1 ? 'border-blue-400 font-semibold text-blue-700' : ''}`}
                                            />
                                          </TableCell>
                                        ))}
                                        <TableCell>
                                          <Button
                                            size="sm"
                                            disabled={saving === `opt-${opt.id}`}
                                            onClick={() => saveOption(opt, activeMulCols.map(c => c.key))}
                                          >
                                            {saving === `opt-${opt.id}` ? '…' : 'Save'}
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ── FIXED COSTS ──────────────────────────────────── */}
              <TabsContent value="fixedcosts">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Fixed Cost Additions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {options.length === 0 ? (
                      <p className="text-sm text-gray-500">No options configured</p>
                    ) : (
                      <div className="space-y-6">
                        {nodes.map(node => {
                          const nodeOpts = optionsForNode(node.id);
                          if (!nodeOpts.length) return null;
                          return (
                            <div key={node.id}>
                              <h4 className="font-semibold text-sm mb-2">
                                Node {node.node_number}: {node.node_title}
                              </h4>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="min-w-[160px]">Option</TableHead>
                                    <TableHead className="min-w-[110px]">Add Cost ($)</TableHead>
                                    <TableHead className="min-w-[110px]">Min ($)</TableHead>
                                    <TableHead className="min-w-[110px]">Max ($)</TableHead>
                                    <TableHead></TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {nodeOpts.map(opt => (
                                    <TableRow key={opt.id}>
                                      <TableCell className="font-medium text-sm">{opt.option_label}</TableCell>
                                      <TableCell>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={optVal(opt, 'add_cost_gbp')}
                                          onChange={e => setOptField(opt.id, 'add_cost_gbp', e.target.value)}
                                          className={`w-28 h-8 text-sm ${parseFloat(optVal(opt, 'add_cost_gbp')) > 0 ? 'border-green-400 font-semibold text-green-700' : ''}`}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={optVal(opt, 'add_cost_min_gbp')}
                                          onChange={e => setOptField(opt.id, 'add_cost_min_gbp', e.target.value)}
                                          className="w-28 h-8 text-sm"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={optVal(opt, 'add_cost_max_gbp')}
                                          onChange={e => setOptField(opt.id, 'add_cost_max_gbp', e.target.value)}
                                          className="w-28 h-8 text-sm"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Button
                                          size="sm"
                                          disabled={saving === `opt-${opt.id}`}
                                          onClick={() => saveOption(opt, ['add_cost_gbp', 'add_cost_min_gbp', 'add_cost_max_gbp'])}
                                        >
                                          {saving === `opt-${opt.id}` ? '…' : 'Save'}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ── QUESTIONS ────────────────────────────────────── */}
              <TabsContent value="questions">
                <div className="space-y-4">
                  {nodes.length === 0 ? (
                    <p className="text-sm text-gray-500">No decision nodes configured</p>
                  ) : nodes.map(node => (
                    <Card key={node.id}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          Node {node.node_number}: {node.node_title}
                          {node.is_entry_node ? <Badge variant="outline">Entry</Badge> : null}
                        </CardTitle>
                        <p className="text-xs text-gray-500 italic">{node.node_question}</p>
                      </CardHeader>
                      <CardContent>
                        {optionsForNode(node.id).length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Key</TableHead>
                                <TableHead>Label</TableHead>
                                <TableHead className="text-right">Add Cost</TableHead>
                                <TableHead>Estimate Override</TableHead>
                                <TableHead>Assessment</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {optionsForNode(node.id).map(opt => (
                                <TableRow key={opt.id}>
                                  <TableCell className="font-mono text-xs">{opt.option_key}</TableCell>
                                  <TableCell className="text-sm">{opt.option_label}</TableCell>
                                  <TableCell className={`text-right text-sm ${parseFloat(opt.add_cost_gbp) > 0 ? 'font-semibold text-green-700' : 'text-gray-400'}`}>
                                    {parseFloat(opt.add_cost_gbp) > 0 ? `$${opt.add_cost_gbp}` : '—'}
                                  </TableCell>
                                  <TableCell className="text-xs">{opt.estimate_type_override || '—'}</TableCell>
                                  <TableCell>
                                    {opt.assessment_required ? (
                                      <Badge variant="outline" className="text-xs">Required</Badge>
                                    ) : <span className="text-gray-400 text-xs">—</span>}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <p className="text-xs text-gray-400">No options</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminElectricalStylePricingEditor;
